import { useEffect, useState } from "react";
import { useAppDispatch } from "../../../redux/features/redux-hooks";
import { resetPassword } from "../../../redux/features/Auth/AuthThunk";
import { Box, Container, Typography, Button, TextField, Link as MuiLink, IconButton, InputAdornment } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from '../../../components/common/SnackBar/snackBar';
import { AxiosError } from 'axios';
import { sendOtp } from "../../../api/AuthAPI";
import useResetPasswordOtpPageStyles from '../../../styles/PublicPage/resetpasswordotppage';
import { PATH_NAME } from "../../../configs/pathName";
import colorConfigs from "../../../configs/colorConfigs";
import loginPageStyles from "../../../styles/PublicPage/login";
import { typo } from "../../../assets/fonts/font";
import registerPageStyles from "../../../styles/PublicPage/register";
import ForgetPasswordPageStyles from "../../../styles/PublicPage/resetPassword";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const ResetPasswordOTPPage = () => {
    const { state } = useLocation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const [timer, setTimer] = useState(60);
    const [canResend, setCanResend] = useState(true);
    const [countdownStarted, setCountdownStarted] = useState(false);

    useEffect(() => {
        if (countdownStarted && timer > 0) {
            const countdown = setTimeout(() => setTimer(timer - 1), 1000);
            return () => clearTimeout(countdown);
        } else if (timer === 0) {
            setCanResend(true);
            setCountdownStarted(false);
        }
    }, [timer, countdownStarted]);

    const phone = state?.phone || "";

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
      };

    const handleResetPassword = async () => {
        if (newPassword && otp) {
            try {
                await dispatch(resetPassword({ new_password: newPassword, otp, full_mobile: phone })).unwrap();
                toast.success("Password reset successfully!");
                navigate(PATH_NAME.LOGIN);
            } catch (error) {
                if (error instanceof AxiosError) {
                    const status = error.response?.status;
                    if (status === 400) {
                        toast.error("Invalid OTP or phone number.");
                    } else if (status === 404) {
                        toast.error("User not found.");
                    } else {
                        toast.error("Failed to reset password.");
                    }
                } else {
                    toast.error("Incorrect OTP.");
                }
            }
        } else {
            toast.error("Please enter both OTP and a new password.");
        }
    };

    const handleResendOtp = async () => {
        try {
            await sendOtp(phone);
            setTimer(30);
            setCanResend(false);
            setCountdownStarted(true);
            toast.success("OTP resent successfully!");
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    return (
        <Box sx={loginPageStyles.container}>
            <Container maxWidth="xs" sx={registerPageStyles.contentContainer}>
                <Typography align="center" sx={{...typo.title, ...ForgetPasswordPageStyles.descriptionText}} >
                    Reset Your Password
                </Typography>
                <Typography align="center" sx={{...typo.bodyContent, ...ForgetPasswordPageStyles.descriptionText}}>
                    Enter the OTP and your new password.
                </Typography>
                <Box sx={useResetPasswordOtpPageStyles.formContainer}>
                <TextField
                        fullWidth
                        variant="outlined"
                        label="OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        inputProps={{
                            maxLength: 6,
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            sx: {
                                ...typo.bodyContent,
                            },
                        }}
                        InputLabelProps={{
                            sx: {
                                ...typo.bodyContent,
                            },
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                    ...typo.bodyContent,
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: colorConfigs.primary,
                                    ...typo.bodyContent,
                                },
                            },
                            "& .MuiInputLabel-root": {},
                            "& .MuiInputLabel-root.Mui-focused": {
                                ...typo.bodyContent,
                            },
                            mb: 2
                        }}
                    />

                    <TextField
                        fullWidth
                        variant="outlined"
                        label="New Password"
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  edge="end"
                                >
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: {
                              input: {
                                ...typo.bodyContent,
                              },
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              ...typo.bodyContent,
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                ...typo.bodyContent,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: colorConfigs.primary,
                                ...typo.bodyContent,
                              },
                            },
                            "& .MuiInputLabel-root": {},
                            "& .MuiInputLabel-root.Mui-focused": {
                              ...typo.bodyContent,
                            },
                            mt:2,
                          }}
                        />
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleResetPassword}
                        sx={{...typo.button, ...useResetPasswordOtpPageStyles.resetButton}}
                        >
                        Reset Password
                    </Button>
                    <Typography align="center" sx={{...typo.button, ...useResetPasswordOtpPageStyles.resendText}}>
                            Didn’t receive the OTP?{' '}
                            {canResend ? (
                                <MuiLink component="button" onClick={handleResendOtp} style={loginPageStyles.signupLink}>
                                    Resend OTP
                                </MuiLink>
                                
                            ) : (
                                <span>Resend OTP in {timer} seconds</span>
                            )}
                        </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default ResetPasswordOTPPage;
